import DOMPurify from 'dompurify'
import { wikiToPlainText, plainToWikiTextMappings, bracketsRegex } from '@/util/tokenize_wiki_text'
import { escapeRegExp } from '@/util/strings'
import { renderMarkdown } from './wiki_text_markdown'

export { toEditableText, fromEditableText }

const EDITABLE_TAGS = ['b', 'br', 'sub', 'sup', 'i', 'ul', 'ol', 'li', 'p', 'u', 'q']
const LINE_START_BLOCK_MARKER = /^( {0,3})(#{1,6}(?=\s|$)|[-+*](?=\s|$)|>|([-*_])[ \t]*(?:\3[ \t]*){2,}(?=[ \t]*$))/gm
const LINE_START_ORDERED_MARKER = /^( {0,3}\d{1,9})([.)])(?=\s|$)/gm
const SUPERSCRIPT_MARKUP = /<sup>([a-zA-Z0-9]+)<\/sup>/g

// The textarea edits plain text: wiki links collapse to their labels and markdown is
// rendered away. Every line break becomes a paragraph break so that line structure
// survives the next render, where a single \n would otherwise be joined into one line.
function toEditableText(wikiText: string): string {
  const allowed = DOMPurify.sanitize(wikiText, { ALLOWED_TAGS: EDITABLE_TAGS })
  const container = document.createElement('div')
  container.innerHTML = renderMarkdown(wikiToPlainText(allowed))
  container.querySelectorAll('br').forEach(lineBreak => lineBreak.replaceWith('\n'))
  return (container.textContent ?? '').replace(/\n/g, '\n\n').replace(/\n{3,}/g, '\n\n').trim()
}

// Restores the wiki links the original text had, by label, onto the edited plain text.
// A link whose label was edited away stays plain text. One pass, longest label first, so a
// short label cannot match inside a link an earlier label already restored.
function fromEditableText(editedText: string, originalWikiText: string): string {
  const plainText = toEditableText(escapeLineStartMarkers(editedText))
  const restored = restoreWikiLinks(plainText, plainToWikiTextMappings(originalWikiText))
  return escapeLineStartMarkers(restoreSuperscripts(restored, originalWikiText))
}

// The textarea shows a superscript the way wikiToPlainText writes it ("4^th"), so put back
// each one the original note had wherever that form is still present. Restored wiki links
// are matched too, and kept as they are, so a caret in a link's target or label survives.
function restoreSuperscripts(plainText: string, originalWikiText: string): string {
  return [...originalWikiText.matchAll(SUPERSCRIPT_MARKUP)].reduce((text, [markup, superscript]) => {
    const shownOrWikiLink = new RegExp(`${bracketsRegex.source}|${escapeRegExp(`^${superscript}`)}(?![a-zA-Z0-9])`, 'g')
    return text.replace(shownOrWikiLink, match => (match.startsWith('[[') ? match : markup))
  }, plainText)
}

function restoreWikiLinks(plainText: string, mappings: Record<string, string>): string {
  const labels = Object.keys(mappings).filter(Boolean).sort((a, b) => b.length - a.length)
  if (!labels.length) return plainText

  const restored = new Set<string>()
  return plainText.replace(new RegExp(labels.map(escapeRegExp).join('|'), 'g'), label => {
    if (restored.has(label)) return label
    restored.add(label)
    return mappings[label]
  })
}

// The textarea is plain text, so a typed "1.", "-", "#" or ">" must stay literal text rather
// than become list, heading or quote structure that the next render would strip.
function escapeLineStartMarkers(text: string): string {
  return text.replace(LINE_START_BLOCK_MARKER, '$1\\$2').replace(LINE_START_ORDERED_MARKER, '$1\\$2')
}
